import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { DirectoryEntry, DirectoryResponse } from '../api/types';
import { describeError } from '../api/client';
import { useApiResource } from '../hooks/useApiResource';
import { EmptyState, ErrorState, LoadingState } from '../components/StateMessage';
import styles from './DirectoryPage.module.css';

/**
 * Name, role, team, department or employee number, matched case-
 * insensitively against one already-fetched, already-authorized list.
 *
 * Purely client-side: the directory endpoint already returns everything
 * this viewer may see (§5.7), so filtering it in the browser adds no new
 * data access and needs no new endpoint — it only narrows what of the same
 * response is currently displayed.
 */
function matchesQuery(employee: DirectoryEntry, query: string): boolean {
  const haystack = [
    employee.fullName,
    employee.currentRoleName,
    employee.currentTeamName,
    employee.currentDepartmentName,
    employee.employeeNumber,
  ]
    .filter((value): value is string => Boolean(value))
    .join(' ')
    .toLowerCase();
  return haystack.includes(query.trim().toLowerCase());
}

/** Up to two initials, the same fallback-avatar convention
 * `ProfilePhoto.tsx` uses on the profile itself (Mission 168). */
function initialsOf(fullName: string): string {
  return fullName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join('');
}

/**
 * People — the directory (docs/UX_ARCHITECTURE.md §2).
 *
 * Identity and placement only. No ratings appear here, deliberately: a list
 * of people with scores beside them is a ranking, and docs/MVP_SCOPE.md §6
 * rules cross-employee ranking out of the MVP explicitly. The judgement
 * work happens on a profile, in context, with its confidence and evidence
 * attached — not in a sortable column.
 *
 * The scope note under the heading matters as much as the list: a manager
 * seeing three people should know they are seeing their own reports, not
 * the whole organisation.
 */
export function DirectoryPage() {
  const state = useApiResource<DirectoryResponse>('/employees');
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    if (state.status !== 'loaded') return [];
    if (!query.trim()) return state.data.employees;
    return state.data.employees.filter((employee) => matchesQuery(employee, query));
  }, [state, query]);

  if (state.status === 'loading') return <LoadingState label="Loading people…" />;

  if (state.status === 'failed') {
    return <ErrorState title="This directory could not be loaded" detail={describeError(state.error)} />;
  }

  const { employees, scope } = state.data;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.heading}>People</h1>
        <p className={styles.scope}>
          {scope === 'organisation'
            ? `Everyone in your organisation · ${employees.length} ${employees.length === 1 ? 'person' : 'people'}`
            : `You and the people you lead · ${employees.length} ${employees.length === 1 ? 'person' : 'people'}`}
        </p>
      </header>

      {employees.length === 0 ? (
        <EmptyState
          title="No people to show"
          detail="Nobody is currently visible to you here. If you expect to see your team, check that they are assigned to a team you lead."
        />
      ) : (
        <>
          <input
            type="search"
            className={styles.search}
            placeholder="Search by name, role, team or department…"
            aria-label="Search people"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />

          {filtered.length === 0 ? (
            <EmptyState
              title="No one matches that search"
              detail={`Nobody visible to you matches "${query.trim()}". Everyone above is still there — try a different search.`}
            />
          ) : (
            <ul className={styles.list}>
              {filtered.map((employee) => (
                <li key={employee.id}>
                  <Link to={`/people/${employee.id}`} className={styles.card}>
                    <span className={styles.nameRow}>
                      <span className={styles.avatar} aria-hidden="true">
                        {employee.photoUrl ? (
                          <img className={styles.avatarImage} src={employee.photoUrl} alt="" />
                        ) : (
                          initialsOf(employee.fullName)
                        )}
                      </span>
                      <span className={styles.name}>{employee.fullName}</span>
                    </span>
                    <span className={styles.role}>
                      {employee.currentRoleName ?? <span className={styles.absent}>No current role</span>}
                    </span>
                    <span className={styles.placement}>
                      {employee.currentTeamName ? (
                        <>
                          {employee.currentTeamName}
                          {employee.currentDepartmentName ? (
                            <span className={styles.department}> · {employee.currentDepartmentName}</span>
                          ) : null}
                        </>
                      ) : (
                        <span className={styles.absent}>Unassigned</span>
                      )}
                    </span>
                    <span className={styles.meta}>
                      {employee.employmentStatus !== 'active' ? (
                        <span className={styles.status}>
                          {employee.employmentStatus === 'leave' ? 'On leave' : 'Former employee'}
                        </span>
                      ) : null}
                      {employee.employeeNumber ? (
                        <span className={styles.number}>{employee.employeeNumber}</span>
                      ) : null}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
